import { Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import LogoIcon from './ui/LogoIcon';
import { cn } from '@/utils/utils';
import { hasAnyPermission, isOrgManager, isSuperAdmin, PERMISSIONS } from '@/utils/permissions';

type NavLeaf = {
  href: string;
  label: string;
  /** Any one of these grants access; omit to leave the item unrestricted. */
  permissions?: string[];
  /** Restrict to super admins regardless of granular permissions. */
  superAdminOnly?: boolean;
  /** Show for super admins plus org admins and PICs. */
  orgManagerOnly?: boolean;
};

type NavItem = {
  label: string;
  icon: React.ReactNode;
  href?: string;
  permissions?: string[];
  superAdminOnly?: boolean;
  orgManagerOnly?: boolean;
  children?: NavLeaf[];
};

type NavSection = {
  header: string;
  items: NavItem[];
};

const Icon = ({ children }: { children: React.ReactNode }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    {children}
  </svg>
);

// Boundary-aware match so a shorter route (e.g. /dashboard/wave) doesn't
// light up as active when a sibling nests under it (e.g. /dashboard/wave/reports).
function isPathActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

const SECTIONS: NavSection[] = [
  {
    header: 'Dashboard',
    items: [
      {
        label: 'Overview',
        href: '/dashboard',
        permissions: [PERMISSIONS.DASHBOARD_VIEW],
        icon: (
          <Icon>
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
          </Icon>
        ),
      },
    ],
  },
  {
    header: 'Insights',
    items: [
      {
        label: 'Activity',
        href: '/dashboard/activity',
        permissions: [PERMISSIONS.ACTIVITIES_VIEW],
        icon: (
          <Icon>
            <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99l1.5 1.5z" />
          </Icon>
        ),
      },
      {
        label: 'Groups',
        href: '/dashboard/groups',
        permissions: [PERMISSIONS.GROUPS_VIEW],
        icon: (
          <Icon>
            <path d="M16 11c1.66 0 3-1.34 3-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05C16.16 13.93 17 15.16 17 16.5V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
          </Icon>
        ),
      },
      // {
      //   label: 'Revenue',
      //   href: '/dashboard/revenue',
      //   icon: (
      //     <Icon>
      //       <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
      //     </Icon>
      //   ),
      // },
      // {
      //   label: 'Goals',
      //   href: '/dashboard/goals',
      //   icon: (
      //     <Icon>
      //       <path d="M12 2L2 7l10 5 10-5-10-5zm0 11.18L4.21 9 2 10l10 5 10-5-2.21-1L12 13.18zM2 15l10 5 10-5-2.21-1.11L12 17.82 4.21 13.89 2 15z" />
      //     </Icon>
      //   ),
      // },
    ],
  },
  {
    header: 'Management',
    items: [
      {
        label: 'Users',
        href: '/dashboard/users',
        permissions: [PERMISSIONS.USERS_VIEW],
        icon: (
          <Icon>
            <path d="M16 11c1.66 0 3-1.34 3-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05C16.16 13.93 17 15.16 17 16.5V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
          </Icon>
        ),
      },
      {
        label: 'Wave',
        icon: (
          <Icon>
            <path d="M17,17c-1.05,0-1.53,0.37-2.13,0.87C14.29,18.35,13.53,19,12,19s-2.29-0.65-2.87-1.13 C8.53,17.37,8.05,17,7,17s-1.53,0.37-2.13,0.87C4.29,18.35,3.53,19,2,19v2c1.53,0,2.29-0.65,2.87-1.13 C5.47,19.37,5.95,19,7,19s1.53,0.37,2.13,0.87C9.71,20.35,10.47,21,12,21s2.29-0.65,2.87-1.13 C15.47,19.37,15.95,19,17,19s1.53,0.37,2.13,0.87C19.71,20.35,20.47,21,22,21v-2c-1.05,0-1.53-0.37-2.13-0.87 C19.29,17.65,18.53,17,17,17z M17,11c-1.05,0-1.53,0.37-2.13,0.87C14.29,12.35,13.53,13,12,13s-2.29-0.65-2.87-1.13 C8.53,11.37,8.05,11,7,11s-1.53,0.37-2.13,0.87C4.29,12.35,3.53,13,2,13v2c1.53,0,2.29-0.65,2.87-1.13 C5.47,13.37,5.95,13,7,13s1.53,0.37,2.13,0.87C9.71,14.35,10.47,15,12,15s2.29-0.65,2.87-1.13 C15.47,13.37,15.95,13,17,13s1.53,0.37,2.13,0.87C19.71,14.35,20.47,15,22,15v-2c-1.05,0-1.53-0.37-2.13-0.87 C19.29,11.65,18.53,11,17,11z M19.87,5.87C19.29,6.35,18.53,7,17,7s-2.29-0.65-2.87-1.13 C13.53,5.37,13.05,5,12,5S10.47,5.37,9.87,5.87C9.29,6.35,8.53,7,7,7S4.71,6.35,4.13,5.87 C3.53,5.37,3.05,5,2,5v2c1.05,0,1.53,0.37,2.13,0.87C4.71,8.35,5.47,9,7,9s2.29-0.65,2.87-1.13 C10.47,7.37,10.95,7,12,7s1.53,0.37,2.13,0.87C14.71,8.35,15.47,9,17,9s2.29-0.65,2.87-1.13 C20.47,7.37,20.95,7,22,7V5C20.95,5,20.47,5.37,19.87,5.87z" />
          </Icon>
        ),
        children: [
          {
            href: '/dashboard/wave/reports',
            label: 'Reports',
            permissions: [PERMISSIONS.WAVES_VIEW],
          },
          {
            href: '/dashboard/wave',
            label: 'All Waves',
            permissions: [PERMISSIONS.WAVES_VIEW],
          },
        ],
      },
    ],
  },
  {
    header: 'Organization',
    items: [
      {
        label: 'Organization',
        orgManagerOnly: true,
        icon: (
          <Icon>
            <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
          </Icon>
        ),
        children: [
          {
            href: '/dashboard/organizations',
            label: 'Organization Management',
            orgManagerOnly: true,
          },
        ],
      },
    ],
  },
  {
    header: 'System',
    items: [
      {
        label: 'Notification',
        icon: (
          <Icon>
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
          </Icon>
        ),
        children: [
          {
            href: '/dashboard/notifications/email-templates',
            label: 'Email Template',
            permissions: [PERMISSIONS.TEMPLATES_VIEW],
          },
          {
            href: '/dashboard/notifications/email-logs',
            label: 'Email Log',
            permissions: [PERMISSIONS.NOTIFICATIONS_VIEW],
          },
          {
            href: '/dashboard/notifications/scheduler',
            label: 'Scheduler',
            permissions: [PERMISSIONS.SCHEDULER_VIEW, PERMISSIONS.SCHEDULED_JOBS_VIEW],
          },
          {
            href: '/dashboard/notifications/weekly-report',
            label: 'Weekly Report',
            permissions: [PERMISSIONS.NOTIFICATIONS_VIEW],
          },
        ],
      },
      {
        label: 'Settings',
        icon: (
          <Icon>
            <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.32-.02-.63-.06-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41L9.25 5.35c-.59.24-1.13.57-1.62.94l-2.39-.96a.488.488 0 00-.59.22L2.74 8.87a.488.488 0 00.12.61l2.03 1.58c-.04.31-.07.62-.07.94 0 .32.02.63.06.94L2.85 14.5a.49.49 0 00-.12.61l1.92 3.32c.13.22.39.31.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.56zM12 15.6A3.6 3.6 0 1112 8.4a3.6 3.6 0 010 7.2z" />
          </Icon>
        ),
        children: [
          {
            href: '/dashboard/settings',
            label: 'General',
          },
          {
            href: '/dashboard/settings/permissions',
            label: 'Permissions',
            permissions: [PERMISSIONS.ROLES_VIEW],
          },
          {
            href: '/dashboard/settings/report-log',
            label: 'Report Log',
            permissions: [PERMISSIONS.REPORTS_VIEW],
          },
        ],
      },
    ],
  },
];

function MenuLeaf({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem & { href: string };
  pathname: string;
  onNavigate?: () => void;
}) {
  const active =
    item.href === '/dashboard' ? pathname === item.href : isPathActive(pathname, item.href);

  return (
    <li className="mt-0.5 w-full">
      <Link
        to={item.href}
        onClick={onNavigate}
        className={cn(
          'mx-4 flex items-center whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
          active
            ? 'bg-gradient-to-tl from-orange-600 to-orange-400 text-white shadow-lg shadow-orange-200'
            : 'text-stone-700 hover:bg-orange-50/70'
        )}
      >
        <span
          className={cn(
            'mr-3 flex h-8 w-8 items-center justify-center rounded-lg bg-white p-2 text-center shadow-sm',
            active ? 'text-orange-600' : 'text-orange-500'
          )}
        >
          {item.icon}
        </span>
        <span className="ml-1">{item.label}</span>
      </Link>
    </li>
  );
}

function MenuParent({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem & { children: NavLeaf[] };
  pathname: string;
  onNavigate?: () => void;
}) {
  // When one child's route nests under another's (e.g. /dashboard/wave and
  // /dashboard/wave/reports), both match by prefix — only the most specific
  // (longest href) should render as the active child.
  const activeChildHref = useMemo(() => {
    const matches = item.children.filter((c) => isPathActive(pathname, c.href));
    if (matches.length === 0) return undefined;
    return matches.reduce((longest, c) => (c.href.length > longest.href.length ? c : longest))
      .href;
  }, [item.children, pathname]);

  const isParentActive = activeChildHref !== undefined;
  const [open, setOpen] = useState(isParentActive);

  useEffect(() => {
    if (isParentActive) setOpen(true);
  }, [isParentActive]);

  return (
    <li className="mt-0.5 w-full">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={cn(
          'mx-4 flex w-[calc(100%-2rem)] items-center whitespace-nowrap rounded-lg px-4 py-2.5 text-left text-sm font-semibold transition-all',
          isParentActive
            ? 'bg-gradient-to-tl from-orange-600 to-orange-400 text-white shadow-lg shadow-orange-200'
            : 'text-stone-700 hover:bg-orange-50/70'
        )}
      >
        <span
          className={cn(
            'mr-3 flex h-8 w-8 items-center justify-center rounded-lg bg-white p-2 text-center shadow-sm',
            isParentActive ? 'text-orange-600' : 'text-orange-500'
          )}
        >
          {item.icon}
        </span>
        <span className="ml-1">{item.label}</span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={cn(
            'ml-auto h-3.5 w-3.5 transition-transform duration-300',
            open ? 'rotate-180' : ''
          )}
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          open ? 'max-h-96' : 'max-h-0'
        )}
      >
        <ul className="mb-0 ml-6 flex list-none flex-col pl-4">
          {item.children.map((child) => {
            const childActive = child.href === activeChildHref;
            return (
              <li className="w-full" key={child.href}>
                <Link
                  to={child.href}
                  onClick={onNavigate}
                  className={cn(
                    'my-0 mr-4 flex items-center rounded-lg py-1.5 pl-4 pr-4 text-sm transition-colors',
                    childActive
                      ? 'font-bold text-stone-900'
                      : 'font-medium text-stone-600 hover:text-orange-600'
                  )}
                >
                  <span className="pointer-events-none mr-4 w-0 text-center opacity-0">
                    {child.label.charAt(0)}
                  </span>
                  <span>{child.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
}

function useVisibleSections(): NavSection[] {
  return useMemo(() => {
    return SECTIONS.map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          if (item.children) {
            const children = item.children.filter(
              (c) =>
                (!c.superAdminOnly || isSuperAdmin()) &&
                (!c.orgManagerOnly || isOrgManager()) &&
                hasAnyPermission(c.permissions ?? [])
            );
            if (children.length === 0) return null;
            return { ...item, children };
          }
          if (item.superAdminOnly && !isSuperAdmin()) return null;
          if (item.orgManagerOnly && !isOrgManager()) return null;
          if (!hasAnyPermission(item.permissions ?? [])) return null;
          return item;
        })
        .filter((item): item is NavItem => item !== null),
    })).filter((section) => section.items.length > 0);
  }, []);
}

function SidebarContent({
  pathname,
  onNavigate,
  onClose,
}: {
  pathname: string;
  onNavigate?: () => void;
  onClose?: () => void;
}) {
  const visibleSections = useVisibleSections();

  return (
    <>
      <div className="relative flex h-20 items-center justify-center">
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="flex items-center gap-3 py-6 text-sm font-semibold whitespace-nowrap"
        >
          <LogoIcon className="h-9 w-9" />
          <span className="h-6 w-px bg-orange-200" aria-hidden="true" />
          <div className="flex flex-col leading-tight">
            <span className="text-base font-extrabold text-stone-800">
              Lifebit
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-orange-500">
              Dashboard
            </span>
          </div>
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="absolute right-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-stone-400 transition hover:bg-orange-50 hover:text-orange-600 lg:hidden"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
              <path strokeWidth="2" strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>

      <hr className="mt-0 h-px border-0 bg-gradient-to-r from-transparent via-orange-200 to-transparent" />

      <div className="block h-auto w-full grow basis-full">
        <ul className="mb-0 flex list-none flex-col pl-0">
          {visibleSections.map((section) => (
            <div key={section.header}>
              <li className="mt-4 w-full">
                <h6 className="ml-2 pl-6 text-xs font-bold uppercase leading-tight tracking-wider text-stone-500/80">
                  {section.header}
                </h6>
              </li>
              {section.items.map((item) =>
                item.children && item.children.length > 0 ? (
                  <MenuParent
                    key={item.label}
                    item={item as NavItem & { children: NavLeaf[] }}
                    pathname={pathname}
                    onNavigate={onNavigate}
                  />
                ) : item.href ? (
                  <MenuLeaf
                    key={item.href}
                    item={item as NavItem & { href: string }}
                    pathname={pathname}
                    onNavigate={onNavigate}
                  />
                ) : null
              )}
            </div>
          ))}
        </ul>
      </div>

      {/* <div className="mt-6 p-4">
        <div className="rounded-2xl bg-gradient-to-br from-orange-100 to-orange-200 p-4">
          <p className="text-xs font-bold text-orange-700">Need a hand?</p>
          <p className="mt-1 text-[11px] leading-snug text-orange-700/80">
            Check the docs or ping the platform team in #lifebit-internal.
          </p>
          <button className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-orange-600 transition-colors hover:bg-orange-50">
            Open docs
          </button>
        </div>
      </div> */}
    </>
  );
}

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ open = false, onClose }: SidebarProps) {
  const { pathname } = useLocation();

  useEffect(() => {
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-stone-900/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 my-4 ml-4 flex w-64 flex-col overflow-y-auto overflow-x-hidden rounded-2xl bg-white shadow-sm ring-1 ring-orange-100 transition-transform duration-300 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-[calc(100%+1rem)]'
        )}
        id="sidenav-main"
      >
        <SidebarContent pathname={pathname} onNavigate={onClose} onClose={onClose} />
      </aside>
    </>
  );
}
